import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, Smartphone } from "lucide-react";
import { Button } from "components/ui/Button";
import { Textarea } from "components/ui/Textarea";
import { SearchableSelect } from "components/ui/SearchableSelect";
import { Skeleton } from "components/ui/Skeleton";
import { cn } from "utils/cn";
import useCaretInsert from "../../cobro/hooks/useCaretInsert";
import { WA_CONNECTION_LABELS } from "../../cobro/utils/collectionReminders";
import {
  BROADCAST_MODES,
  BROADCAST_TEXT_MAX_LENGTH,
  BROADCAST_VARIABLES,
  renderPreview,
  templateTextFor,
  unknownVariablesIn,
} from "./broadcastMessage";

// Ruta de Configuración → Integraciones → Canales, donde el operador conecta su número.
const CHANNELS_SETTINGS_PATH = "/admin/settings?tab=integrations";

/**
 * Qué se envía y desde dónde: número de WhatsApp del operador, texto (predefinido o libre)
 * y vista previa con los datos de un paquete de la búsqueda.
 *
 * Props:
 *   numbers   { options, loading, error } — opciones de `broadcastNumberOptions`; `error` es
 *             el texto del backend o `true` (falló sin texto)
 *   value     { integrationId, mode, text }
 *   onChange  (partial) => void
 *   sampleItem  ítem de la vista previa para la muestra (o null)
 *   disabled  boolean
 */
export default function BroadcastComposer({ numbers, value, onChange, sampleItem, disabled }) {
  const { t } = useTranslation();
  const textRef = useRef(null);
  const setText = (text) => onChange({ text });
  const insertVariable = useCaretInsert(textRef, value.text, setText);

  const isFreeText = value.mode === BROADCAST_MODES.FREE_TEXT;
  const unknown = isFreeText ? unknownVariablesIn(value.text) : [];
  const length = (value.text || "").length;
  const template = templateTextFor(value.mode, value.text);
  const previewText = renderPreview(template, sampleItem);
  const selectedNumber = numbers.options.find((o) => o.id === value.integrationId);

  const fieldLabel = "text-xs font-semibold text-fg-muted";

  return (
    <div className="flex flex-col gap-4">
      {/* ── Número ── */}
      <div className="flex flex-col gap-1.5">
        <span className={fieldLabel}>{t("Enviar desde")}</span>
        {numbers.loading ? (
          <Skeleton className="h-10 w-full" />
        ) : numbers.error ? (
          <p className="m-0 text-sm text-status-error" role="alert">
            {numbers.error === true ? t("No se pudieron cargar los números de WhatsApp.") : numbers.error}
          </p>
        ) : !numbers.options.length ? (
          <div className="flex flex-col gap-2 rounded-xl border border-status-warning/30 bg-status-warning-soft p-3 text-sm">
            <span className="flex items-center gap-2 font-semibold text-status-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {t("No hay un número de WhatsApp para notificaciones")}
            </span>
            <span className="text-fg-muted">
              {t("Las comunicaciones salen desde el WhatsApp del operador. Conecta un número con uso de notificaciones o ambos.")}
            </span>
            <Link to={CHANNELS_SETTINGS_PATH} className="font-semibold text-brand-600 hover:underline">
              {t("Conectar un número")} →
            </Link>
          </div>
        ) : (
          <SearchableSelect
            placeholder={t("Elige un número")}
            isDisabled={disabled}
            options={numbers.options.map((o) => ({
              value: o.id,
              label: o.usable ? o.label : `${o.label} · ${t(WA_CONNECTION_LABELS[o.connection] || "Desconectado")}`,
              isDisabled: !o.usable,
            }))}
            value={value.integrationId}
            onChange={(integrationId) => onChange({ integrationId })}
          />
        )}
      </div>

      {/* ── Mensaje ── */}
      <div className="flex flex-col gap-1.5">
        <span className={fieldLabel}>{t("Mensaje")}</span>
        <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t("Tipo de mensaje")}>
          {[
            { mode: BROADCAST_MODES.ORDEN_DEMORADA, label: t("Orden demorada") },
            { mode: BROADCAST_MODES.FREE_TEXT, label: t("Texto libre") },
          ].map((option) => {
            const active = value.mode === option.mode;
            return (
              <button
                key={option.mode}
                type="button"
                role="radio"
                aria-checked={active}
                disabled={disabled}
                onClick={() => onChange({ mode: option.mode })}
                className={cn(
                  "rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50",
                  active ? "border-brand-500 bg-brand-50 text-brand-700" : "border-border bg-bg text-fg"
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {isFreeText && (
        <div className="flex flex-col gap-2">
          <Textarea
            ref={textRef}
            rows={6}
            value={value.text}
            disabled={disabled}
            maxLength={BROADCAST_TEXT_MAX_LENGTH}
            invalid={unknown.length > 0}
            aria-label={t("Texto del mensaje")}
            placeholder={t("Hola {nombre}, te escribimos por tu pedido {seguimiento}…")}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex items-center justify-between gap-2 text-xs">
            {unknown.length ? (
              <span className="text-status-error" role="alert">
                {t("Variables desconocidas: {{list}}", { list: unknown.map((v) => `{${v}}`).join(", ") })}
              </span>
            ) : (
              <span className="text-fg-muted">{t("Toca una variable para insertarla donde está el cursor.")}</span>
            )}
            <span className="shrink-0 tabular-nums text-fg-muted">
              {length}/{BROADCAST_TEXT_MAX_LENGTH}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {BROADCAST_VARIABLES.map((variable) => (
              <Button
                key={variable.name}
                size="sm"
                variant="outline"
                disabled={disabled}
                title={t(variable.labelKey)}
                onClick={() => insertVariable(`{${variable.name}}`)}
              >
                <code className="text-xs">{`{${variable.name}}`}</code>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* ── Vista previa ── */}
      <div className="flex flex-col gap-1.5">
        <span className={fieldLabel}>{t("Vista previa")}</span>
        <div className="flex flex-col gap-1.5 rounded-[2px_14px_14px_14px] bg-status-success-soft p-3 text-sm">
          <p className="m-0 flex items-center gap-1.5 text-xs font-bold text-status-success">
            <Smartphone className="h-3.5 w-3.5" />
            {selectedNumber?.label || t("Número del operador")}
          </p>
          <p className="m-0 whitespace-pre-wrap break-words leading-relaxed text-fg" data-testid="broadcast-preview">
            {previewText || t("Escribe el mensaje para ver la vista previa.")}
          </p>
        </div>
        <p className="m-0 text-xs text-fg-muted">
          {sampleItem
            ? t("Con los datos de un paquete de la búsqueda. Cada destinatario lo recibe con los suyos.")
            : t("Busca paquetes para ver la muestra con datos reales.")}
        </p>
      </div>
    </div>
  );
}
